import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { GoogleMap, useJsApiLoader, Polygon, Polyline, Marker, MarkerClusterer, OverlayView } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { Spin, Button, Tooltip, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    PlusOutlined,
    MinusOutlined,
    LockOutlined,
    UnlockOutlined,
    GoldOutlined,
    CheckOutlined,
    DoubleLeftOutlined,
    DoubleRightOutlined,
    ArrowLeftOutlined,
    FileAddOutlined,
    PlusCircleOutlined,
    ToolOutlined
} from '@ant-design/icons';
import PickupAppointmentsContent from '../appointment/PickupAppointmentsContent';
import { PickupAppointmentType } from '../appointment/graphql/queries/getPickupAppointmentListing';
import PickupLayout from "../layouts/PickupLayout";
import dayjs from "dayjs";
import { timestampToDate } from "../../../utils/dateTimeUtils";
import { hasResource } from "../../../utils/resourceAuth";
import { ResourceCode } from "../../../constants/resourceCodes";

// 节流函数
const throttle = <T extends (...args: any[]) => any>(func: T, delay: number): T => {
    let lastCall = 0;
    return ((...args: Parameters<T>) => {
        const now = Date.now();
        if (now - lastCall >= delay) {
            lastCall = now;
            return func(...args);
        }
    }) as T;
};

// 预览线组件 - 使用 React.memo 避免不必要的重渲染
const DrawPreviewLines = React.memo<{
    mousePosition: google.maps.LatLngLiteral | null;
    polygonPaths: google.maps.LatLngLiteral[];
}>(({ mousePosition, polygonPaths }) => {
    if (!mousePosition || polygonPaths.length === 0) return null;

    return (
        <>
            {/* 从最后一个点到鼠标位置的预览线 */}
            <Polyline
                path={[polygonPaths[polygonPaths.length - 1], mousePosition]}
                options={{
                    strokeColor: '#A78BFA',
                    strokeOpacity: 0.6,
                    strokeWeight: 2,
                    clickable: false,
                }}
            />
            {/* 如果有2个或以上的点，显示从鼠标位置到第一个点的预览线 */}
            {polygonPaths.length >= 2 && (
                <Polyline
                    path={[mousePosition, polygonPaths[0]]}
                    options={{
                        strokeColor: '#A78BFA',
                        strokeOpacity: 0.6,
                        strokeWeight: 2,
                        clickable: false,
                    }}
                />
            )}
        </>
    );
});

// 默认中心点（洛杉矶）
const defaultCenter = {
    lat: 34.0522,
    lng: -118.2437
};

type MapMode = 'none' | 'move' | 'draw';

// 地图选项 - 隐藏所有控件，根据模式动态设置交互
const getMapOptions = (mode: MapMode): google.maps.MapOptions => ({
    mapTypeControl: false, // 隐藏地图/卫星视图切换按钮
    streetViewControl: false, // 隐藏街景小人
    fullscreenControl: false, // 隐藏全屏按钮
    zoomControl: false, // 隐藏缩放控制按钮
    draggable: mode === 'move', // 只有移动模式可拖动
    scrollwheel: true, // 启用滚轮缩放
    disableDoubleClickZoom: true, // 禁用双击缩放
    gestureHandling: mode === 'none' ? 'none' : 'greedy', // 绘制和移动模式允许交互
    clickableIcons: false, // 禁用POI图标点击
    draggableCursor: mode === 'draw' ? 'crosshair' : 'default', // 绘制模式显示十字准星
});

// 预定义50种高对比度、鲜艳且易于区分的颜色 (移除相似色、浅色及灰色系)
const PREDEFINED_COLORS: string[] = [
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF',
    '#00FFFF', '#FF8000', '#8000FF', '#00FF80', '#FF0080',
    '#80FF00', '#0080FF', '#FF0040', '#00FF40', '#4000FF',
    '#FFD700', '#32CD32', '#1E90FF', '#FF1493', '#00CED1',
    '#FF4500', '#7FFF00', '#4169E1', '#E91E63', '#00FA9A',
    '#FFA500', '#9932CC', '#ADFF2F', '#FF69B4', '#20B2AA',
    '#DC143C', '#00FF7F', '#8A2BE2', '#FFD800', '#00BFFF',
    '#800000', '#008000', '#000080', '#808000', '#800080',
    '#008080', '#B22222', '#228B22', '#0000CD', '#C71585',
    '#FF7F50', '#9ACD32', '#6495ED', '#DDA0DD', '#2E8B57'
];
// 注：部分深色(如#2F4F4F)虽然饱和度略低，但其深色感与地图背景及默认灰(#6B7280)有显著区分。

// 图标缓存 Map (全局缓存，避免组件卸载后重建)
const iconCache = new Map<string, google.maps.Icon>();

// 提取单个 Marker 组件以利用 React.memo
interface AppointmentMarkerProps {
    coordinateKey: string;
    position: google.maps.LatLngLiteral;
    groupAppointments: PickupAppointmentType[];
    isSelected: boolean;
    taskColor: string;
    selectionOrderNum?: number;
    useSystemRouting: boolean;
    onClick: (ids: number[]) => void;
    onMouseOver: (group: PickupAppointmentType[], pos: google.maps.LatLngLiteral) => void;
    onMouseOut: () => void;
}

const AppointmentMarker = React.memo((props: AppointmentMarkerProps) => {
    const {
        coordinateKey,
        position,
        groupAppointments,
        isSelected,
        taskColor,
        selectionOrderNum,
        useSystemRouting,
        onClick,
        onMouseOver,
        onMouseOut
    } = props;

    // 检查该组中是否有 CREATED 状态的预约单
    const hasCreatedAppointments = useMemo(() => groupAppointments.some(apt => apt.status === 'CREATED'), [groupAppointments]);

    // 如果是重合坐标（多个预约单），优先显示stopNumber，否则显示重合个数
    const overlapCount = groupAppointments.length;

    // 检查重合组中是否有非CREATED状态的预约单且有stopNumber
    const hasStopNumber = useMemo(() => groupAppointments.some(apt =>
        apt.status !== 'CREATED' && apt.stopNumber !== undefined && apt.stopNumber !== null
    ), [groupAppointments]);

    // 确定显示内容
    let displayStopNumber: number | undefined;
    let displayStopNumberSeq: number | undefined;
    let displayOverlapCount: number | undefined;
    let displaySelectionOrder: number | undefined;

    const firstAppointment = groupAppointments[0];

    // 获取选择顺序号
    if (hasCreatedAppointments && isSelected) {
        displaySelectionOrder = selectionOrderNum;
    }

    if (overlapCount > 1) {
        if (hasStopNumber) {
            // 有stopNumber时，显示第一个有效的stopNumber和stopNumberSeq
            const firstWithStopNumber = groupAppointments.find(apt =>
                apt.status !== 'CREATED' && apt.stopNumber !== undefined && apt.stopNumber !== null
            );
            displayStopNumber = firstWithStopNumber?.stopNumber;
            displayStopNumberSeq = firstWithStopNumber?.stopNumberSeq;
            displayOverlapCount = undefined;
        } else {
            // 没有stopNumber时，显示重合个数
            displayStopNumber = undefined;
            displayStopNumberSeq = undefined;
            displayOverlapCount = overlapCount;
        }
    } else {
        // 单个预约单
        displayStopNumber = hasCreatedAppointments ? undefined : firstAppointment.stopNumber;
        displayStopNumberSeq = hasCreatedAppointments ? undefined : firstAppointment.stopNumberSeq;
        displayOverlapCount = undefined;
    }

    // 检查是否有新创建的预约单 (保留原逻辑接口，暂不启用)
    const isNew = false;

    // 确定渲染颜色：选中状态使用紫色 (#8B5CF6)，否则使用任务分配色
    const finalColor = isSelected ? '#8B5CF6' : taskColor;

    // 生成图标的 Key
    const iconKey = `${finalColor}_${displayStopNumber ?? 'x'}_${displayStopNumberSeq ?? 'x'}_${displayOverlapCount ?? 'x'}_${isNew}_${displaySelectionOrder ?? 'x'}_${useSystemRouting}_${isSelected}`;

    // 获取或生成图标
    let icon = iconCache.get(iconKey);
    if (!icon) {
        let displayNumber = '';

        if (useSystemRouting) {
            // 系统排线开启：不显示选择顺序号，只显示 stopNumber
            if (displayStopNumber !== undefined && displayStopNumber !== null) {
                if (displayStopNumberSeq !== undefined && displayStopNumberSeq !== null && displayStopNumberSeq !== 0) {
                    displayNumber = `${displayStopNumber}-${displayStopNumberSeq}`;
                } else {
                    displayNumber = displayStopNumber.toString();
                }
            }
        } else {
            // 系统排线关闭：优先显示选择顺序号
            if (displaySelectionOrder !== undefined && displaySelectionOrder !== null) {
                displayNumber = displaySelectionOrder.toString();
            } else if (displayStopNumber !== undefined && displayStopNumber !== null) {
                if (displayStopNumberSeq !== undefined && displayStopNumberSeq !== null && displayStopNumberSeq !== 0) {
                    displayNumber = `${displayStopNumber}-${displayStopNumberSeq}`;
                } else {
                    displayNumber = displayStopNumber.toString();
                }
            }
        }

        const displayText = displayOverlapCount && displayOverlapCount > 1 ? `${displayOverlapCount}-M` : displayNumber;
        const isOverlapMarker = displayOverlapCount && displayOverlapCount > 1;

        // 简化 SVG：改用简单的圆形和路径，减少浏览器渲染负担
        icon = {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="46" viewBox="0 0 36 46">
                ${isOverlapMarker ?
                    `<circle cx="18" cy="18" r="16" fill="${finalColor}" stroke="white" stroke-width="2"/>` :
                    `<path d="M18 2C10.3 2 4 8.3 4 16c0 10.5 14 21.5 14 21.5s14-11 14-21.5c0-7.7-6.3-14-14-14z" fill="${finalColor}" stroke="white" stroke-width="1.5"/>
                     <circle cx="18" cy="16" r="10" fill="white"/>`
                }
                ${displayText ? `<text x="18" y="21" font-size="11" fill="${isOverlapMarker ? 'white' : finalColor}" text-anchor="middle" font-weight="bold" font-family="Arial">${displayText}</text>` : ''}
            </svg>
        `),
            scaledSize: { width: 36, height: 46 } as google.maps.Size,
            anchor: { x: 18, y: 46 } as google.maps.Point,
        };
        iconCache.set(iconKey, icon);
    }

    // 处理点击事件
    const handleGroupClick = useCallback(() => {
        if (!hasCreatedAppointments) return;

        // 找到所有 CREATED 状态的 ID 进行批量回传
        const createdIds = groupAppointments
            .filter(apt => apt.status === 'CREATED')
            .map(apt => apt.id);

        if (createdIds.length > 0) {
            onClick(createdIds);
        }
    }, [hasCreatedAppointments, groupAppointments, onClick]);

    // 只有当有 CREATED 状态时才响应交互
    if (!hasCreatedAppointments) {
        // 非 CREATED 状态（如已完成）也可以显示，但通常不可点击，除非是只读查看
        // 原代码逻辑：clickable={hasCreatedAppointments}
    }

    return (
        <Marker
            key={`group-${coordinateKey}`}
            position={position}
            icon={icon}
            clickable={true} // 固定开启 clickable，以确保 hover 事件能被捕获
            options={{
                cursor: hasCreatedAppointments ? 'pointer' : 'default'
            }}
            onClick={handleGroupClick}
            zIndex={isSelected ? 1000 : (hasCreatedAppointments ? 500 : 100)} // 选中最高，未调度(可点击)高于已调度
            onMouseOver={() => onMouseOver(groupAppointments, position)}
            onMouseOut={onMouseOut}
        />
    );
}, (prev: AppointmentMarkerProps, next: AppointmentMarkerProps) => {
    // 自定义比较函数，确保最小化渲染
    // 只有当这些属性改变时才重渲染
    return (
        prev.isSelected === next.isSelected &&
        prev.taskColor === next.taskColor &&
        prev.selectionOrderNum === next.selectionOrderNum &&
        prev.useSystemRouting === next.useSystemRouting &&
        prev.groupAppointments === next.groupAppointments && // 引用比较，父组件需保证未变时不换引用
        prev.coordinateKey === next.coordinateKey
    );
});

const MapRoutingPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [appointments, setAppointments] = useState<PickupAppointmentType[]>([]);
    const [listSelectedKeys, setListSelectedKeys] = useState<React.Key[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());//选中的地图点位
    const [selectionOrder, setSelectionOrder] = useState<Map<number, number>>(new Map()); // 选中顺序映射：ID -> 顺序号
    const [mapMode, setMapMode] = useState<MapMode>('move'); // 地图模式：默认为移动模式
    const [polygonPaths, setPolygonPaths] = useState<google.maps.LatLngLiteral[]>([]); // 多边形路径
    const [drawnPolygons, setDrawnPolygons] = useState<google.maps.LatLngLiteral[][]>([]); // 已绘制的多边形
    const [mousePosition, setMousePosition] = useState<google.maps.LatLngLiteral | null>(null); // 鼠标位置（用于预览线）
    const [drawVersion, setDrawVersion] = useState(0); // 绘制版本号，用于强制卸载组件
    const [isListPanelOpen, setIsListPanelOpen] = useState(true); // 右侧列表面板是否展开
    const [buttonHandlers, setButtonHandlers] = useState<{
        handleCreateAppointment?: () => void;
        handleAddTask?: () => void;
        handleCreateTask?: () => void;
    }>({}); // 存储右侧列表的按钮处理函数
    const [isPastDate, setIsPastDate] = useState<boolean>(false); // 存储右侧列表的 isPastDate 值
    const [useSystemRouting, setUseSystemRouting] = useState<boolean>(true); // 存储右侧列表的 useSystemRouting 值

    // 悬停信息窗口状态
    const [hoveredInfo, setHoveredInfo] = useState<{
        position: google.maps.LatLngLiteral;
        content: React.ReactNode;
    } | null>(null);

    // 悬停定时器引用
    const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

    const fitBoundsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Google Maps API Key
    const GOOGLE_MAPS_API_KEY = 'AIzaSyD8huYA1dL8uG9O1a77bnW3JtpTjUimjmc';

    // 使用 useJsApiLoader 代替 LoadScript
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        preventGoogleFontsLoading: true,
    });

    const onLoad = useCallback((map: google.maps.Map) => {
        setMap(map);
    }, []);

    const onUnmount = useCallback(() => {
        setMap(null);
    }, []);

    // 处理返回预约单页面
    const handleBackToAppointments = useCallback(() => {
        try {
            // 获取当前的查询参数，保持筛选条件
            const searchParams = new URLSearchParams(location.search);

            // 构建预约单页面的URL，传递相同的筛选参数
            const appointmentUrl = '/pickup/appointment';
            // 导航回预约单页面
            navigate(appointmentUrl, { replace: true });
        } catch (error) {
            console.error('返回预约单页面失败:', error);
            message.error('返回预约单页面失败，请重试');
        }
    }, [navigate, location.search]);

    // 接收预约单数据
    const handleAppointmentsUpdate = useCallback((newAppointments: PickupAppointmentType[]) => {
        console.log('📍 地图接收到预约单数据:', newAppointments.length, '条');
        console.log('📍 第一条数据示例:', newAppointments[0]);
        setAppointments(newAppointments);

        // 清除已绘制的区域和绘制状态
        setDrawnPolygons([]);
        setPolygonPaths([]);
        setMousePosition(null);

        // ⚠️ 重要：数据更新时，不清空选中状态，保留用户的选择
        // 原因：清空选中状态会通过 onSelectionChange 通知列表，导致列表的选择被误清空
        // setListSelectedKeys([]);
        // setSelectedIds(new Set());
        // setSelectionOrder(new Map());

        // 重置地图模式为移动模式，确保清除所有绘制相关的线条
        setMapMode('move');
    }, []);

    // ===== iconCache 内存管理逻辑 =====
    useEffect(() => {
        const cleanupInterval = setInterval(() => {
            if (iconCache.size > 200) {
                console.log('🧹 正在清理 iconCache，当前大小:', iconCache.size);
                // 高级清理策略：保留最新的 100 个
                const keys = Array.from(iconCache.keys());
                const keysToRemove = keys.slice(0, keys.length - 100);
                keysToRemove.forEach(key => iconCache.delete(key));
            }
        }, 60000); // 每分钟检查一次

        return () => clearInterval(cleanupInterval);
    }, []);

    // 过滤有经纬度的预约单，并排除 CANCELLED 状态 (提前定义以供后续使用)
    const appointmentsWithLocation = useMemo(() => {
        const filtered = appointments.filter(apt =>
            apt.cooperationWarehouseLatitude !== null &&
            apt.cooperationWarehouseLatitude !== undefined &&
            apt.cooperationWarehouseLongitude !== null &&
            apt.cooperationWarehouseLongitude !== undefined &&
            apt.status !== 'CANCELLED'
        );
        return filtered;
    }, [appointments]);

    // 检测重合坐标并分组 - 缓存逻辑
    const coordinateGroups = useMemo(() => {
        if (!isLoaded || appointmentsWithLocation.length === 0) return new Map<string, PickupAppointmentType[]>();

        const coordinateMap = new Map<string, PickupAppointmentType[]>();

        appointmentsWithLocation.forEach(appointment => {
            if (appointment.cooperationWarehouseLatitude && appointment.cooperationWarehouseLongitude) {
                const roundedLat = Math.round(appointment.cooperationWarehouseLatitude * 10000) / 10000;
                const roundedLng = Math.round(appointment.cooperationWarehouseLongitude * 10000) / 10000;
                const coordinateKey = `${roundedLat},${roundedLng}`;

                if (!coordinateMap.has(coordinateKey)) {
                    coordinateMap.set(coordinateKey, []);
                }
                coordinateMap.get(coordinateKey)!.push(appointment);
            }
        });

        return coordinateMap;
    }, [isLoaded, appointmentsWithLocation]);

    // coordinateGroups 已移动到上方

    // 接收按钮处理函数
    const handleButtonHandlersUpdate = useCallback((handlers: {
        handleCreateAppointment?: () => void;
        handleAddTask?: () => void;
        handleCreateTask?: () => void;
    }) => {
        setButtonHandlers(handlers);
    }, []);

    // 接收 isPastDate 值
    const handleIsPastDateUpdate = useCallback((isPastDateValue: boolean) => {
        setIsPastDate(isPastDateValue);
    }, []);

    // 接收 useSystemRouting 值
    const handleUseSystemRoutingUpdate = useCallback((useSystemRoutingValue: boolean) => {
        setUseSystemRouting(useSystemRoutingValue);
    }, []);

    // coordinateGroups 已移动到上方

    // 自动定焦到选中的标记
    const focusOnSelectedMarkers = useCallback((selectedIds: Set<number>) => {
        if (!map || !isLoaded || selectedIds.size === 0) {
            return;
        }

        console.log('🎯 开始自动定焦到选中的标记:', selectedIds.size, '个');

        // 找到所有选中标记的坐标
        const selectedPositions: google.maps.LatLngLiteral[] = [];

        appointmentsWithLocation.forEach(appointment => {
            if (selectedIds.has(appointment.id) &&
                appointment.cooperationWarehouseLatitude !== null &&
                appointment.cooperationWarehouseLatitude !== undefined &&
                appointment.cooperationWarehouseLongitude !== null &&
                appointment.cooperationWarehouseLongitude !== undefined) {
                selectedPositions.push({
                    lat: appointment.cooperationWarehouseLatitude,
                    lng: appointment.cooperationWarehouseLongitude
                });
            }
        });

        if (selectedPositions.length === 0) {
            console.log('⚠️ 没有找到选中标记的有效坐标');
            return;
        }

        console.log('🎯 找到', selectedPositions.length, '个选中标记的坐标');

        // 如果只有一个标记，使用平滑动画定焦到该点
        if (selectedPositions.length === 1) {
            const position = selectedPositions[0];

            // 使用平滑的动画过渡
            map.panTo(position);

            // 延迟设置缩放级别，确保平移动画完成后再缩放
            setTimeout(() => {
                const currentZoom = map.getZoom() || 10;
                const targetZoom = Math.max(currentZoom, 16); // 提高缩放级别以便更好地查看单个标记

                if (currentZoom < targetZoom) {
                    map.setZoom(targetZoom);
                }
            }, 300);

            console.log('🎯 定焦到单个标记:', position);
            return;
        }

        // 如果有多个标记，使用边界框包含所有选中的标记
        const bounds = new google.maps.LatLngBounds();
        selectedPositions.forEach(position => {
            bounds.extend(position);
        });

        // 计算边界框的大小，动态调整边距
        const northeast = bounds.getNorthEast();
        const southwest = bounds.getSouthWest();
        const latDiff = northeast.lat() - southwest.lat();
        const lngDiff = northeast.lng() - southwest.lng();

        // 根据边界框大小动态调整边距（最小0.005度，最大0.02度）
        const dynamicPadding = Math.max(0.005, Math.min(0.02, Math.max(latDiff, lngDiff) * 0.2));

        const expandedNortheast = {
            lat: northeast.lat() + dynamicPadding,
            lng: northeast.lng() + dynamicPadding
        };
        const expandedSouthwest = {
            lat: southwest.lat() - dynamicPadding,
            lng: southwest.lng() - dynamicPadding
        };

        const expandedBounds = new google.maps.LatLngBounds(expandedSouthwest, expandedNortheast);

        // 使用平滑的动画过渡到边界框
        map.fitBounds(expandedBounds, {
            top: 20,
            right: 20,
            bottom: 20,
            left: 20
        });

        console.log('🎯 定焦到多个标记的边界框:', expandedBounds.toJSON(), '动态边距:', dynamicPadding);
    }, [map, isLoaded, appointmentsWithLocation]);

    // 处理列表选中变化（列表 → 地图同步）
    const handleListSelectionChange = useCallback((selectAppointmentMap: Map<any, any>) => {
        console.log('📋→📍 列表选中变化通知地图:', selectAppointmentMap.size, '个状态变化');

        // 同步更新两个状态，确保一致性
        const newListSelectedKeys: React.Key[] = [];
        const newSelectedIds = new Set<number>();

        selectAppointmentMap.forEach((isSelected: boolean, id: number) => {
            if (isSelected) {
                newListSelectedKeys.push(id);
                newSelectedIds.add(id);
            }
        });

        console.log('📍 同步后地图选中状态:', {
            listSelectedKeys: newListSelectedKeys.length,
            selectedIds: newSelectedIds.size
        });

        setListSelectedKeys(newListSelectedKeys);
        setSelectedIds(newSelectedIds);


        // 更新选择顺序
        const newSelectionOrder = new Map<number, number>();
        newListSelectedKeys.forEach((id, idx) => {
            newSelectionOrder.set(id as number, idx + 1);
        });
        setSelectionOrder(newSelectionOrder);

        // 自动定焦到选中的标记
        if (map && isLoaded && newSelectedIds.size > 0) {
            focusOnSelectedMarkers(newSelectedIds);
        }
    }, [map, isLoaded, focusOnSelectedMarkers]);

    // 生成100种不同的颜色（使用HSL色相均匀分布）
    const generateColors = useCallback((count: number): string[] => {
        const colors: string[] = [];
        const goldenRatioConjugate = 0.618033988749895; // 黄金比例共轭数，用于更好的颜色分布

        for (let i = 0; i < count; i++) {
            // 使用黄金比例来分布色相，避免相邻颜色过于相似
            const hue = (i * goldenRatioConjugate * 360) % 360;

            // 根据色相调整饱和度和亮度，确保颜色鲜艳且可见
            // 避免紫色区域(270-290度)，因为紫色用于选中状态
            let saturation = 70; // 默认饱和度
            let lightness = 50;  // 默认亮度

            // 对于某些色相范围，调整参数以获得更好的视觉效果
            if (hue >= 45 && hue <= 65) {
                // 黄色区域，降低亮度以便于查看
                lightness = 45;
                saturation = 75;
            } else if (hue >= 270 && hue <= 290) {
                // 紫色区域，稍微偏移以区别于选中紫色(#8B5CF6对应约270度)
                saturation = 60;
                lightness = 55;
            } else if (hue >= 0 && hue <= 30) {
                // 红色区域
                saturation = 75;
                lightness = 52;
            } else if (hue >= 120 && hue <= 140) {
                // 绿色区域
                saturation = 65;
                lightness = 48;
            }

            // 将HSL转换为RGB，然后转换为十六进制
            const h = hue / 360;
            const s = saturation / 100;
            const l = lightness / 100;

            let r, g, b;
            if (s === 0) {
                r = g = b = l;
            } else {
                const hue2rgb = (p: number, q: number, t: number) => {
                    if (t < 0) t += 1;
                    if (t > 1) t -= 1;
                    if (t < 1 / 6) return p + (q - p) * 6 * t;
                    if (t < 1 / 2) return q;
                    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                    return p;
                };

                const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
                const p = 2 * l - q;
                r = hue2rgb(p, q, h + 1 / 3);
                g = hue2rgb(p, q, h);
                b = hue2rgb(p, q, h - 1 / 3);
            }

            const toHex = (x: number) => {
                const hex = Math.round(x * 255).toString(16);
                return hex.length === 1 ? '0' + hex : hex;
            };

            colors.push(`#${toHex(r)}${toHex(g)}${toHex(b)}`);
        }

        return colors;
    }, []);

    // 为不同的 taskNumber 分配颜色 - 使用预定义数组
    const taskColors = useMemo(() => {
        if (appointmentsWithLocation.length === 0) return new Map<string, string>();

        const taskColorMap = new Map<string, string>();

        // 提取唯一的任务编号
        const uniqueTaskNumbers = new Set<string>();
        for (const apt of appointmentsWithLocation) {
            if (apt.taskNumber) uniqueTaskNumbers.add(apt.taskNumber);
        }

        let index = 0;
        uniqueTaskNumbers.forEach((taskNumber) => {
            taskColorMap.set(taskNumber, PREDEFINED_COLORS[index % PREDEFINED_COLORS.length]);
            index++;
        });

        console.log(`📍 发现 ${uniqueTaskNumbers.size} 个不同的任务编号，使用预定义颜色映射`);

        return taskColorMap;
    }, [appointmentsWithLocation]);



    // ⭐ 监控并修复状态不一致问题（只监听 listSelectedKeys 变化，避免循环）
    useEffect(() => {
        // 确保 selectedIds 和 listSelectedKeys 始终一致
        const expectedIds = new Set(listSelectedKeys as number[]);

        // 比较当前 selectedIds 和预期的 selectedIds
        const currentSize = selectedIds.size;
        const expectedSize = expectedIds.size;

        // 只在确实不一致时才更新（避免无限循环）
        if (currentSize !== expectedSize) {
            console.warn('⚠️ 检测到状态大小不一致，正在修复:', {
                当前: currentSize,
                应该为: expectedSize
            });
            setSelectedIds(expectedIds);

            // 同步更新选择顺序
            const newSelectionOrder = new Map<number, number>();
            listSelectedKeys.forEach((id, idx) => {
                newSelectionOrder.set(id as number, idx + 1);
            });
            setSelectionOrder(newSelectionOrder);
            return;
        }

        // 检查内容是否一致
        let hasContentDiff = false;
        for (const id of listSelectedKeys) {
            if (!selectedIds.has(id as number)) {
                hasContentDiff = true;
                break;
            }
        }

        if (hasContentDiff) {
            console.warn('⚠️ 检测到状态内容不一致，正在修复');
            setSelectedIds(expectedIds);

            // 同步更新选择顺序
            const newSelectionOrder = new Map<number, number>();
            listSelectedKeys.forEach((id, idx) => {
                newSelectionOrder.set(id as number, idx + 1);
            });
            setSelectionOrder(newSelectionOrder);
        }
    }, [listSelectedKeys]); // 只依赖 listSelectedKeys，避免 selectedIds 变化导致循环

    // 判断点是否在多边形内（射线投射算法）
    const isPointInPolygon = (point: google.maps.LatLngLiteral, polygon: google.maps.LatLngLiteral[]): boolean => {
        let inside = false;
        const x = point.lng;
        const y = point.lat;

        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const xi = polygon[i].lng;
            const yi = polygon[i].lat;
            const xj = polygon[j].lng;
            const yj = polygon[j].lat;

            const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }

        return inside;
    };

    // createMarkerIcon generated locally in AppointmentMarker now

    // 生成hub点位标记图标（只在 Google Maps API 加载完成后创建）
    const hubIcon = useMemo((): google.maps.Icon | undefined => {
        if (!isLoaded) return undefined;

        const fillColor = '#3B82F6'; // 蓝色
        return {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="64" viewBox="0 0 48 64">
                <!-- 房子屋顶 -->
                <polygon points="24,2 2,20 46,20" fill="${fillColor}" stroke="#FFFFFF" stroke-width="1"/>
                <!-- 房子主体 -->
                <rect x="4" y="20" width="40" height="36" fill="${fillColor}" stroke="#FFFFFF" stroke-width="1"/>
                <!-- 门 -->
                <rect x="18" y="38" width="12" height="18" fill="#FFFFFF" rx="2"/>
            </svg>
        `),
            scaledSize: { width: 32, height: 42 } as google.maps.Size,
            anchor: { x: 16, y: 42 } as google.maps.Point,
        };
    }, [isLoaded]);

    // coordinateGroups 已在此前定义

    // 生成绘制点位标记图标
    const createDrawPointIcon = useCallback((index: number): google.maps.Icon => {
        return {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" fill="#8B5CF6" stroke="#FFFFFF" stroke-width="2"/>
                    <text x="12" y="16" font-size="12" fill="#FFFFFF" text-anchor="middle" font-weight="bold">${index + 1}</text>
                </svg>
            `),
            scaledSize: { width: 24, height: 24 } as google.maps.Size,
            anchor: { x: 12, y: 12 } as google.maps.Point,
        };
    }, []);

    // 使用 useMemo 获取唯一的仓库列表
    const uniqueWarehouses = useMemo(() => {
        const warehouseMap = new Map<number, {
            id: number;
            lat: number;
            lng: number;
            name: string;
        }>();

        appointments.forEach(appointment => {
            if (
                appointment.warehouseId &&
                appointment.warehouseLatitude != null &&
                appointment.warehouseLongitude != null &&
                !warehouseMap.has(appointment.warehouseId)
            ) {
                warehouseMap.set(appointment.warehouseId, {
                    id: appointment.warehouseId,
                    lat: appointment.warehouseLatitude,
                    lng: appointment.warehouseLongitude,
                    name: appointment.warehouseName || 'Warehouse',
                });
            }
        });

        return Array.from(warehouseMap.values());
    }, [appointments]);

    // 处理标记点击（地图 → 列表同步）- 支持批量选中/取消选中
    const handleMarkerClick = useCallback((appointmentIds: number[]) => {
        console.log('📍→📋 地图标记点击:', appointmentIds);

        if (!appointmentIds || appointmentIds.length === 0) return;

        setListSelectedKeys(prev => {
            const newKeys = [...prev];

            // 检查是否所有点击的ID都已经选中
            const allSelected = appointmentIds.every(id => newKeys.includes(id));
            let updatedKeys;

            if (allSelected) {
                // 如果全部已选中，则全部取消选中
                updatedKeys = newKeys.filter(key => !appointmentIds.includes(key as number));
                console.log(`📍 批量取消选中 ${appointmentIds.length} 个, 剩余:`, updatedKeys.length);
            } else {
                // 如果有未选中的，则全部选中（补齐）
                const toAdd = appointmentIds.filter(id => !newKeys.includes(id));
                newKeys.push(...toAdd);
                updatedKeys = newKeys;
                console.log(`📍 批量新增选中 ${toAdd.length} 个, 共:`, updatedKeys.length);
            }

            // 同步更新 selectedIds
            setSelectedIds(new Set(updatedKeys as number[]));


            // 更新选择顺序
            const newSelectionOrder = new Map<number, number>();
            updatedKeys.forEach((id, idx) => {
                newSelectionOrder.set(id as number, idx + 1);
            });
            setSelectionOrder(newSelectionOrder);

            return updatedKeys;
        });
    }, []);

    // 自动调整地图视图
    useEffect(() => {
        if (!map || !isLoaded || appointmentsWithLocation.length === 0) {
            return;
        }

        if (fitBoundsTimerRef.current) {
            clearTimeout(fitBoundsTimerRef.current);
        }

        fitBoundsTimerRef.current = setTimeout(() => {
            console.log('🗺️ 开始调整地图视图，包含', appointmentsWithLocation.length, '个标记');

            const bounds = new google.maps.LatLngBounds();
            appointmentsWithLocation.forEach((apt, index) => {
                if (apt.cooperationWarehouseLatitude && apt.cooperationWarehouseLongitude) {
                    const position = {
                        lat: apt.cooperationWarehouseLatitude,
                        lng: apt.cooperationWarehouseLongitude
                    };
                    bounds.extend(position);
                    // console.log(`🗺️ 添加位置 ${index + 1}:`, position);
                }
            });

            console.log('🗺️ 地图边界:', bounds.toJSON());
            map.fitBounds(bounds);

            // 单个点时设置合适的缩放
            if (appointmentsWithLocation.length === 1) {
                setTimeout(() => {
                    const zoom = map.getZoom();
                    console.log('🗺️ 当前缩放级别:', zoom);
                    if (zoom && zoom > 15) {
                        map.setZoom(15);
                        console.log('🗺️ 调整缩放级别到 15');
                    }
                }, 100);
            }
        }, 200);

        return () => {
            if (fitBoundsTimerRef.current) {
                clearTimeout(fitBoundsTimerRef.current);
            }
        };
    }, [map, isLoaded, appointmentsWithLocation]);

    // 当地图模式改变时，更新地图选项
    useEffect(() => {
        if (map) {
            map.setOptions(getMapOptions(mapMode));
        }
    }, [map, mapMode]);

    // 处理放大
    const handleZoomIn = () => {
        if (map) {
            const currentZoom = map.getZoom();
            if (currentZoom !== undefined) {
                map.setZoom(currentZoom + 1);
            }
        }
    };

    // 处理缩小
    const handleZoomOut = () => {
        if (map) {
            const currentZoom = map.getZoom();
            if (currentZoom !== undefined) {
                map.setZoom(currentZoom - 1);
            }
        }
    };

    // 处理移动模式切换
    const handleMoveMode = () => {
        if (mapMode === 'move') {
            // 如果是移动模式（开锁），点击后锁定地图
            setMapMode('none');
        } else {
            // 如果是锁定状态，点击后解锁允许移动
            setMapMode('move');
        }
    };

    // 处理绘制模式
    const handleDrawMode = () => {
        if (mapMode === 'draw') {
            // 如果已经是绘制模式，再次点击则关闭
            setMapMode('none');
            setPolygonPaths([]);
            setMousePosition(null);
        } else {
            // 激活绘制模式
            setMapMode('draw');
            setPolygonPaths([]);
            setMousePosition(null);

        }
    };

    // 完成绘制 - 优化性能
    const handleCompleteDrawing = useCallback(() => {
        if (polygonPaths.length >= 3) {
            console.log('🎨 完成绘制，开始查找区域内点位...');
            const startTime = performance.now();

            // ⭐⭐⭐ 关键：保存当前路径的副本，然后立即清除所有绘制状态
            const currentPolygonPaths = [...polygonPaths];

            // 使用 flushSync 强制同步更新状态，立即触发重新渲染，彻底消除残留
            // 不添加到 drawnPolygons，避免显示蓝色多边形
            flushSync(() => {
                setMousePosition(null);
                setPolygonPaths([]);
                setDrawnPolygons([]);
                setMapMode('none');
            });

            // 使用 requestAnimationFrame 异步处理，避免阻塞UI
            requestAnimationFrame(() => {
                // 查找并选中多边形内的所有点位（仅 CREATED 状态）
                const selectedAppointments: PickupAppointmentType[] = [];

                appointmentsWithLocation.forEach(appointment => {
                    // 只处理 CREATED 状态的预约单
                    if (appointment.status !== 'CREATED') {
                        return;
                    }

                    const point = {
                        lat: appointment.cooperationWarehouseLatitude!,
                        lng: appointment.cooperationWarehouseLongitude!
                    };

                    // 检查点是否在新绘制的多边形内（使用保存的路径副本）
                    if (isPointInPolygon(point, currentPolygonPaths)) {
                        selectedAppointments.push(appointment);
                    }
                });

                const endTime = performance.now();
                console.log(`🎨 点位检测完成，耗时 ${(endTime - startTime).toFixed(2)}ms，找到 ${selectedAppointments.length} 个 CREATED 状态的点位`);

                // ⭐ 按照坐标距离warehouse从远到近排序：优先经度，其次纬度
                const sortedAppointments = selectedAppointments.sort((a, b) => {
                    // 获取坐标和warehouse的经纬度
                    const aCoordLat = a.cooperationWarehouseLatitude!;
                    const aCoordLng = a.cooperationWarehouseLongitude!;
                    const bCoordLat = b.cooperationWarehouseLatitude!;
                    const bCoordLng = b.cooperationWarehouseLongitude!;
                    const aWarehouseLat = a.warehouseLatitude;
                    const aWarehouseLng = a.warehouseLongitude;
                    const bWarehouseLat = b.warehouseLatitude;
                    const bWarehouseLng = b.warehouseLongitude;

                    // 如果warehouse经纬度不存在，放到最后
                    if (aWarehouseLat === null || aWarehouseLat === undefined ||
                        aWarehouseLng === null || aWarehouseLng === undefined) {
                        return 1;
                    }
                    if (bWarehouseLat === null || bWarehouseLat === undefined ||
                        bWarehouseLng === null || bWarehouseLng === undefined) {
                        return -1;
                    }

                    // 计算经度距离（绝对值，因为坐标可能在warehouse左边或右边）
                    const aLngDistance = Math.abs(aCoordLng - aWarehouseLng);
                    const bLngDistance = Math.abs(bCoordLng - bWarehouseLng);

                    // 优先按经度距离排序（从近到远，距离小的在前）
                    if (aLngDistance !== bLngDistance) {
                        return aLngDistance - bLngDistance;
                    }

                    // 如果经度距离相同，再按纬度距离排序（从近到远，距离小的在前）
                    const aLatDistance = Math.abs(aCoordLat - aWarehouseLat);
                    const bLatDistance = Math.abs(bCoordLat - bWarehouseLat);
                    return aLatDistance - bLatDistance;
                });

                const selectedAppointmentIds = sortedAppointments.map(apt => apt.id);

                // ⭐ 关键：同步更新两个状态，确保一致性
                setListSelectedKeys(prev => {
                    const newKeysSet = new Set([...prev, ...selectedAppointmentIds]);
                    const updatedKeys = Array.from(newKeysSet);

                    console.log('🎨 绘制完成后选中状态:', {
                        原有: prev.length,
                        新增: selectedAppointmentIds.length,
                        合并后: updatedKeys.length
                    });

                    // 同步更新 selectedIds
                    setSelectedIds(new Set(updatedKeys as number[]));

                    // 更新选择顺序
                    const newSelectionOrder = new Map<number, number>();
                    updatedKeys.forEach((id, idx) => {
                        newSelectionOrder.set(id as number, idx + 1);
                    });
                    setSelectionOrder(newSelectionOrder);

                    return updatedKeys;
                });

                // 显示选中结果提示
                if (selectedAppointmentIds.length > 0) {
                    message.success(`已选中区域内的 ${selectedAppointmentIds.length} 个 CREATED 状态的点位`);
                } else {
                    message.info('区域内没有找到 CREATED 状态的点位');
                }
            });
        } else {
            // 如果点数不足3个，直接清除状态
            flushSync(() => {
                setMousePosition(null);
                setPolygonPaths([]);
                setDrawnPolygons([]);
                setMapMode('none');
            });
        }
    }, [polygonPaths, appointmentsWithLocation, isPointInPolygon]);

    // 处理地图点击（绘制模式下）
    const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (mapMode === 'draw' && e.latLng) {
            const newPoint = {
                lat: e.latLng.lat(),
                lng: e.latLng.lng()
            };
            setPolygonPaths([...polygonPaths, newPoint]);
        }
    }, [mapMode, polygonPaths]);

    // 处理鼠标移动（绘制模式下显示预览线）- 使用更激进的节流策略
    const handleMouseMove = useMemo(
        () => {
            const delay = appointmentsWithLocation.length > 500 ? 250 :
                appointmentsWithLocation.length > 200 ? 200 : 150;
            return throttle((e: google.maps.MapMouseEvent) => {
                if (mapMode === 'draw' && e.latLng && polygonPaths.length > 0) {
                    setMousePosition({
                        lat: e.latLng.lat(),
                        lng: e.latLng.lng()
                    });
                }
            }, delay);
        },
        [mapMode, polygonPaths.length, appointmentsWithLocation.length]
    );

    // 处理右键点击（绘制模式下快速完成绘制）
    const handleRightClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (mapMode === 'draw' && polygonPaths.length >= 3) {
            e.stop(); // 阻止默认的右键菜单
            handleCompleteDrawing();
        }
    }, [mapMode, polygonPaths.length, handleCompleteDrawing]);

    // ===== 3. 延迟悬停逻辑 (Debounced Hover) =====
    const handleMarkerMouseOver = useCallback((groupAppointments: PickupAppointmentType[], position: google.maps.LatLngLiteral) => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
        }

        // 设置新的定时器，500ms 后显示详情
        hoverTimerRef.current = setTimeout(() => {
            // 1. 提取唯一的任务单号
            const taskNumbers = Array.from(new Set(groupAppointments.map(apt => apt.taskNumber || "")))
                .filter(Boolean)
                .join(', ');

            // 2. 提取唯一的司机信息（名称+账号）
            const drivers = Array.from(new Set(groupAppointments.map(apt => {
                const name = apt.driverName || "";
                const account = apt.driverAccount ? `(${apt.driverAccount})` : "";
                return (name || account) ? `${name}${account}` : "";
            })))
                .filter(Boolean)
                .join(', ');

            // 3. 提取站点信息列表 (ID: 名称)
            const stationLines = groupAppointments.map(apt => {
                const id = apt.cooperationWarehouseId || "";
                const name = apt.cooperationWarehouseName || "";
                return (id || name) ? `${id}: ${name}` : "";
            }).filter(Boolean);

            // 4. 提取唯一的地址信息
            const addresses = Array.from(new Set(groupAppointments.map(apt => {
                const line1 = apt.addressLine1 || "";
                const line2 = apt.addressLine2 ? ` ${apt.addressLine2}` : "";
                const zip = apt.zipcode ? ` ${apt.zipcode}` : "";
                return `${line1}${line2}${zip}`.trim();
            })))
                .filter(Boolean)
                .join(', ');

            const isZh = i18n.language === 'zh';
            const colon = isZh ? '：' : ': ';

            const lines: string[] = [];

            // 如果有任务号，显示任务号和司机行（带标签）
            if (taskNumbers) {
                lines.push(`${t('pickup.appointment.columns.taskNumber')}${colon}${taskNumbers}`);
            }
            if (drivers) {
                lines.push(`${t('pickup.progress.columns.driver')}${colon}${drivers}`);
            }

            // 站点信息行（不带“站点：”前缀，直接展示数据）
            if (stationLines.length > 0) {
                // 如果之前有任务/司机行，可以在中间空一行或直接拼接
                // 根据图片显示，直接拼接即可
                stationLines.forEach(line => lines.push(line));
            }

            // 地址信息行
            if (addresses) {
                lines.push(`${t('pickup.appointment.columns.address')}${colon}${addresses}`);
            }

            const finalContent = lines.join('\n');

            setHoveredInfo({
                position,
                content: finalContent,
            });
        }, 500); // 延迟 500ms
    }, [i18n, t]);

    const handleMarkerMouseOut = useCallback(() => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
        setHoveredInfo(null);
    }, []);

    // ===== 提前计算渲染标记列表 (Scheme A + Scheme B) - 移动至依赖定义后修复 TDZ =====
    const renderedMarkers = useMemo(() => {
        if (!isLoaded || coordinateGroups.size === 0) return null;

        return Array.from(coordinateGroups.entries()).map(([coordinateKey, groupAppointments]) => {
            const firstAppointment = groupAppointments[0];
            const position = {
                lat: firstAppointment.cooperationWarehouseLatitude!,
                lng: firstAppointment.cooperationWarehouseLongitude!
            };

            const isGroupSelected = groupAppointments.some(apt => selectedIds.has(apt.id));
            const latestSelectedNum = isGroupSelected ?
                Math.max(...groupAppointments.map(apt => selectionOrder.get(apt.id) || 0)) : undefined;

            const taskColor = groupAppointments[0].taskNumber ?
                taskColors.get(groupAppointments[0].taskNumber) || '#6B7280' : '#6B7280';

            return (
                <AppointmentMarker
                    key={`group-${coordinateKey}`}
                    coordinateKey={coordinateKey}
                    position={position}
                    groupAppointments={groupAppointments}
                    isSelected={isGroupSelected}
                    taskColor={taskColor}
                    selectionOrderNum={latestSelectedNum}
                    useSystemRouting={useSystemRouting}
                    onClick={handleMarkerClick}
                    onMouseOver={handleMarkerMouseOver}
                    onMouseOut={handleMarkerMouseOut}
                />
            );
        });
    }, [isLoaded, coordinateGroups, selectedIds, selectionOrder, taskColors, useSystemRouting, handleMarkerClick, handleMarkerMouseOver, handleMarkerMouseOut]);

    // ===== 使用 useMemo 缓存渲染组件，提升性能 =====

    // coordinateGroups 已在上方通过 useMemo 定义并优化缓存逻辑

    // 缓存绘制点标记
    const drawPointMarkers = useMemo(() => {
        if (!isLoaded || mapMode !== 'draw') return null;

        return polygonPaths.map((point, index) => (
            <Marker
                key={`draw-point-${index}`}
                position={point}
                icon={createDrawPointIcon(index)}
                clickable={false}
            />
        ));
    }, [isLoaded, mapMode, polygonPaths, createDrawPointIcon]);

    // 缓存绘制连接线
    const drawConnectionLine = useMemo(() => {
        if (!isLoaded || mapMode !== 'draw' || polygonPaths.length <= 1) return null;

        return (
            <Polyline
                path={polygonPaths}
                options={{
                    strokeColor: '#8B5CF6',
                    strokeOpacity: 1,
                    strokeWeight: 3,
                    clickable: false,
                }}
            />
        );
    }, [isLoaded, mapMode, polygonPaths]);

    // 缓存绘制多边形填充
    const drawPolygonFill = useMemo(() => {
        if (!isLoaded || mapMode !== 'draw' || polygonPaths.length < 3) return null;

        return (
            <Polygon
                paths={polygonPaths}
                options={{
                    fillColor: '#8B5CF6',
                    fillOpacity: 0.15,
                    strokeColor: 'transparent',
                    strokeOpacity: 0,
                    strokeWeight: 0,
                    clickable: false,
                }}
            />
        );
    }, [isLoaded, mapMode, polygonPaths]);

    // 缓存已完成的多边形
    const completedPolygons = useMemo(() => {
        if (!isLoaded || drawnPolygons.length === 0) return null;

        return drawnPolygons.map((polygon, index) => (
            <Polygon
                key={`completed-polygon-${index}`}
                paths={polygon}
                options={{
                    fillColor: '#3B82F6',
                    fillOpacity: 0.3,
                    strokeColor: '#3B82F6',
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    clickable: false,
                }}
            />
        ));
    }, [isLoaded, drawnPolygons]);

    return (
        <div className="h-screen w-screen overflow-hidden bg-gray-100">
            <div className="absolute inset-0 overflow-hidden">
                {/* 左侧：地图区域 - 根据右侧面板状态动态调整宽度 */}
                <div
                    className="relative transition-all duration-300 h-full flex flex-col"
                    style={{ width: isListPanelOpen ? '50%' : '100%' }}
                >
                    {/* 顶部留白 - 与筛选区域顶部对齐 */}
                    <div className="flex-shrink-0" style={{ height: '16px' }}></div>

                    {/* 地图主体区域 */}
                    <div className="flex-1 relative">
                        {/* 返回按钮 - 左上角 */}
                        <div className="absolute top-4 left-4 z-10">
                            <Tooltip title={t('mapRouting.return')} placement="right">
                                <Button
                                    type="default"
                                    shape="circle"
                                    icon={<ArrowLeftOutlined />}
                                    size="large"
                                    onClick={handleBackToAppointments}
                                    className="shadow-lg bg-white hover:bg-gray-50"
                                />
                            </Tooltip>
                        </div>

                        {/* 地图控制按钮组 */}
                        {isLoaded && !loadError && (
                            <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
                                {/* 放大按钮 */}
                                <Tooltip title={t('mapRouting.max')} placement="left">
                                    <Button
                                        type="default"
                                        shape="circle"
                                        icon={<PlusOutlined />}
                                        size="large"
                                        onClick={handleZoomIn}
                                        className="shadow-lg bg-white"
                                    />
                                </Tooltip>

                                {/* 缩小按钮 */}
                                <Tooltip title={t('mapRouting.min')} placement="left">
                                    <Button
                                        type="default"
                                        shape="circle"
                                        icon={<MinusOutlined />}
                                        size="large"
                                        onClick={handleZoomOut}
                                        className="shadow-lg bg-white"
                                    />
                                </Tooltip>

                                {/* 分隔线 */}
                                <div className="h-px bg-gray-300 mx-2"></div>

                                {/* 移动模式切换按钮 */}
                                <Tooltip title={mapMode === 'move' ? t('mapRouting.lock') : t('mapRouting.unlock')} placement="left">
                                    <Button
                                        type={mapMode === 'move' ? 'default' : 'primary'}
                                        shape="circle"
                                        icon={mapMode === 'move' ? <UnlockOutlined style={{ fontSize: '20px' }} /> : <LockOutlined style={{ fontSize: '20px' }} />}
                                        size="large"
                                        onClick={handleMoveMode}
                                        className={mapMode === 'move' ? 'shadow-lg bg-white' : 'shadow-lg'}
                                        style={mapMode === 'none' ? { backgroundColor: '#8B5CF6', borderColor: '#8B5CF6' } : {}}
                                    />
                                </Tooltip>

                                {/* 绘制区域按钮 */}
                                <Tooltip title={t('mapRouting.draw')} placement="left">
                                    <Button
                                        type={mapMode === 'draw' ? 'primary' : 'default'}
                                        shape="circle"
                                        icon={<GoldOutlined style={{ fontSize: '20px' }} />}
                                        size="large"
                                        onClick={handleDrawMode}
                                        className="shadow-lg"
                                    />
                                </Tooltip>

                                {/* 完成绘制按钮 - 仅在绘制模式下显示 */}
                                {mapMode === 'draw' && polygonPaths.length >= 3 && (
                                    <Tooltip title="完成绘制" placement="left">
                                        <Button
                                            type="primary"
                                            shape="circle"
                                            icon={<CheckOutlined />}
                                            size="large"
                                            onClick={handleCompleteDrawing}
                                            className="shadow-lg bg-green-500 hover:bg-green-600 border-green-500"
                                        />
                                    </Tooltip>
                                )}

                                {/* 分隔线 */}
                                <div className="h-px bg-gray-300 mx-2"></div>

                                {/* 创建预约单按钮 */}
                                <Tooltip title={t('pickup.appointment.buttons.createAppointment')} placement="left">
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={<FileAddOutlined />}
                                        size="large"
                                        onClick={buttonHandlers.handleCreateAppointment}
                                        disabled={isPastDate}
                                        className="shadow-lg bg-blue-500 hover:bg-blue-600 border-blue-500"
                                    />
                                </Tooltip>

                                {/* 添加任务按钮 */}
                                <Tooltip title={t('pickup.appointment.buttons.addTask')} placement="left">
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={<PlusCircleOutlined />}
                                        size="large"
                                        onClick={buttonHandlers.handleAddTask}
                                        disabled={isPastDate || !hasResource([ResourceCode.menu.PICKUP_APPOINTMENT_OPERATE])}
                                        className="shadow-lg bg-orange-500 hover:bg-orange-600 border-orange-500"
                                    />
                                </Tooltip>

                                {/* 创建任务按钮 */}
                                <Tooltip title={t('pickup.appointment.buttons.createTask')} placement="left">
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={<PlusOutlined />}
                                        size="large"
                                        onClick={buttonHandlers.handleCreateTask}
                                        disabled={isPastDate || !hasResource([ResourceCode.menu.PICKUP_APPOINTMENT_OPERATE])}
                                        className="shadow-lg bg-purple-500 hover:bg-purple-600 border-purple-500"
                                    />
                                </Tooltip>
                            </div>
                        )}

                        <div className="absolute inset-0">
                            {loadError ? (
                                <div className="flex items-center justify-center h-full bg-gray-100">
                                    <div className="text-center p-8">
                                        <p className="text-red-500 mb-2">
                                            {t('mapRouting.apiKeyMissing')}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                            Error loading Google Maps
                                        </p>
                                    </div>
                                </div>
                            ) : !isLoaded ? (
                                <div className="flex items-center justify-center h-full bg-gray-100">
                                    <Spin size="large" tip="Loading Map..." />
                                </div>
                            ) : (
                                <GoogleMap
                                    mapContainerStyle={{ width: '100%', height: '100%' }}
                                    center={defaultCenter}
                                    zoom={10}
                                    options={getMapOptions(mapMode)}
                                    onLoad={onLoad}
                                    onUnmount={onUnmount}
                                    onClick={handleMapClick}
                                    onMouseMove={handleMouseMove}
                                    onRightClick={handleRightClick}
                                >
                                    {/* 渲染仓库标记 */}
                                    {isLoaded && hubIcon && uniqueWarehouses.map(warehouse => (
                                        <Marker
                                            key={`warehouse-${warehouse.id}`}
                                            position={{ lat: warehouse.lat, lng: warehouse.lng }}
                                            icon={hubIcon}
                                            title={warehouse.name}
                                            zIndex={100}
                                        />
                                    ))}

                                    {/* 预约单标记 */}
                                    {renderedMarkers}

                                    {isLoaded && appointmentsWithLocation.length === 0 && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            background: 'white',
                                            padding: '20px',
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                            zIndex: 1000
                                        }}>
                                            <p style={{ margin: 0, color: '#666' }}>暂无标记点位数据</p>
                                            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#999' }}>
                                                {/*请确保列表中有预约单数据且包含经纬度信息*/}
                                            </p>
                                        </div>
                                    )}

                                    {/* 渲染正在绘制的点标记 */}
                                    {drawPointMarkers}

                                    {/* 渲染正在绘制的连接线 */}
                                    {drawConnectionLine}

                                    {/* 渲染鼠标位置的预览线 - 使用独立组件优化性能 */}
                                    {isLoaded && mapMode === 'draw' && (
                                        <DrawPreviewLines
                                            mousePosition={mousePosition}
                                            polygonPaths={polygonPaths}
                                        />
                                    )}

                                    {/* 渲染正在绘制的多边形填充（当有3个或以上的点时） */}
                                    {drawPolygonFill}

                                    {/* 渲染已完成的多边形 */}
                                    {completedPolygons}

                                    {/* 渲染悬停信息窗口 (自定义暗色 Tooltip 样式) */}
                                    {hoveredInfo && (
                                        <OverlayView
                                            position={hoveredInfo.position}
                                            mapPaneName={OverlayView.FLOAT_PANE}
                                            getPixelPositionOffset={(width, height) => ({
                                                x: -(width / 2),
                                                y: -(height + 55), // 向上偏移，避开 Marker (假设 Marker 高度约 52)
                                            })}
                                        >
                                            <div style={{
                                                background: 'rgba(0, 0, 0, 0.7)',
                                                color: 'white',
                                                padding: '10px 15px',
                                                borderRadius: '4px',
                                                fontSize: '13px',
                                                lineHeight: '1.5',
                                                whiteSpace: 'pre-wrap',
                                                width: 'max-content',   // 宽度自适应内容，确保不被压缩
                                                maxWidth: '600px',      // 允许更宽的展示，防止单号换行
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                                pointerEvents: 'none',
                                                zIndex: 1000
                                            }}>
                                                {hoveredInfo.content}
                                            </div>
                                        </OverlayView>
                                    )}
                                </GoogleMap>
                            )}
                        </div>

                        {/* 底部留白 - 与分页栏+操作栏高度对齐 */}
                        <div className="flex-shrink-0" style={{ height: '82px' }}></div>
                    </div>
                </div>

                {/* 面板切换按钮 - 始终固定在右侧 */}
                <div
                    className="absolute top-1/2 -translate-y-1/2 z-30 transition-all duration-300"
                    style={{
                        right: isListPanelOpen ? 'calc(50% - 16px)' : '8px'
                    }}
                >
                    <Tooltip title={isListPanelOpen ? t('mapRouting.packup') : t('mapRouting.unfold')} placement="left">
                        <div
                            onClick={() => setIsListPanelOpen(!isListPanelOpen)}
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                            style={{
                                fontSize: '32px',
                                color: '#8B5CF6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {isListPanelOpen ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
                        </div>
                    </Tooltip>
                </div>

                {/* 右侧：Appointment 页面内容 - 滑动面板 */}
                <div
                    className={`absolute bg-gray-50 shadow-2xl transition-transform duration-300 z-20 ${isListPanelOpen ? 'translate-x-0' : 'translate-x-full'
                        }`}
                    style={{
                        top: 0,
                        right: 0,
                        width: '50%',
                        height: '100%'
                    }}
                >
                    <div className="h-full overflow-auto">
                        <PickupAppointmentsContent
                            onAppointmentsUpdate={handleAppointmentsUpdate}
                            externalSelectedKeys={listSelectedKeys}
                            onSelectionChange={handleListSelectionChange}
                            onButtonHandlersUpdate={handleButtonHandlersUpdate}
                            onIsPastDateUpdate={handleIsPastDateUpdate}
                            onUseSystemRoutingUpdate={handleUseSystemRoutingUpdate}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MapRoutingPage;
